import React, { memo, useEffect, useMemo, useState } from 'react';
import isString from 'lodash/isString';
import replace from 'lodash/replace';
import set from 'lodash/set';
import { useSelector } from 'react-redux';
import { HashRouter, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { StepsSection } from '@yme/molecules/steps-section';
import { getCommercetoolsProjectKey } from '@yme/selectors/common';
import { getRootLang } from '@yme/selectors/index';
import { DEFAULT_LOCALE, useTranslation } from '@yme/services/i18n';
import { HeroSection } from './components/HeroSection';
import { StepRoutes, STORAGE_RETURN_FORM_KEY } from './constants';
import { getHeroTitle, getStepNumber, removeHashCharacter, showStepsNavBar } from './helpers';
import { OrderStep, PackageStep, SelectItemsStep, ShippingStep } from './steps';
import { FinalStep } from './steps/FinalStep';
import { StepsSectionContainerStyled } from './styled';
export const ReturnForm = memo((props) => {
    const { returnType, successPageModel, errorPageModel, webToCaseUrl, supportLink, heroImage, yourOrder, yourParcel, selectItems, shippingLabel, } = props;
    const { t } = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const modelLanguage = useSelector(getRootLang);
    const commercetoolsProject = useSelector(getCommercetoolsProjectKey);
    const currentRouteName = removeHashCharacter(location.hash);
    const locale = replace(modelLanguage || DEFAULT_LOCALE, '-', '_');
    const { origin, pathname } = window?.location;
    const currentPageUrl = `${origin}${pathname}`;
    const redirectFormPageUrl = `${webToCaseUrl}${currentPageUrl}`;
    const pagePath = replace(pathname, /(.\w+)$/g, '');
    const { returnReasonsEn } = selectItems;
    const [cachedData, setCachedData] = useState(() => {
        const cached = sessionStorage.getItem(STORAGE_RETURN_FORM_KEY);
        if (!cached) {
            return {};
        }
        const parsed = JSON.parse(cached);
        if (isString(parsed.pickupDate)) {
            set(parsed, 'pickupDate', new Date(parsed.pickupDate));
        }
        return parsed;
    });
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, [location.hash]);
    useEffect(() => {
        try {
            sessionStorage.setItem(STORAGE_RETURN_FORM_KEY, JSON.stringify(cachedData));
        }
        catch (err) {
            sessionStorage.setItem(STORAGE_RETURN_FORM_KEY, '');
        }
    }, [cachedData]);
    const stepSectionLabels = useMemo(() => ({
        firstStepTitle: t('returnForm_yourOrderStep_title'),
        secondStepTitle: t('returnForm_yourPackageStep_title'),
        thirdStepTitle: t('returnForm_selectItemsStep_title'),
        fourthStepTitle: t('returnForm_shippingLabelStep_title'),
    }), [t]);
    if (!webToCaseUrl || !yourOrder.returnPolicy || !heroImage) {
        return null;
    }
    return (React.createElement(HashRouter, { hashType: 'noslash' },
        React.createElement(HeroSection, { image: heroImage, title: getHeroTitle(currentRouteName, t) }),
        showStepsNavBar(currentRouteName) && (React.createElement(StepsSectionContainerStyled, null,
            React.createElement(StepsSection, { labels: stepSectionLabels, pageNumber: getStepNumber(currentRouteName), isRightToLeft: false }, null))),
        React.createElement(Switch, null,
            React.createElement(Route, { exact: true, path: "/" },
                React.createElement(Redirect, { to: `/${StepRoutes.ORDER_STEP}` })),
            React.createElement(Route, { path: `/${StepRoutes.ORDER_STEP}` },
                React.createElement(OrderStep, { stepData: yourOrder, locale: locale, commercetoolsProject: commercetoolsProject, cachedData: cachedData, redirectFormPageUrl: redirectFormPageUrl, supportLink: supportLink, setCachedData: setCachedData })),
            React.createElement(Route, { path: `/${StepRoutes.PACKAGE_STEP}` },
                React.createElement(PackageStep, { stepData: yourParcel, commercetoolsProject: commercetoolsProject, returnType: returnType, cachedData: cachedData, redirectFormPageUrl: redirectFormPageUrl, setCachedData: setCachedData })),
            React.createElement(Route, { path: `/${StepRoutes.SELECT_ITEMS_STEP}` },
                React.createElement(SelectItemsStep, { stepData: selectItems, cachedData: cachedData, setCachedData: setCachedData })),
            React.createElement(Route, { path: `/${StepRoutes.SHIPPING_STEP}` },
                React.createElement(ShippingStep, { returnType: returnType, returnReasonsEn: returnReasonsEn, stepData: shippingLabel, cachedData: cachedData, pagePath: pagePath, setCachedData: setCachedData })),
            React.createElement(Route, { path: `/${StepRoutes.END_STEP_SUCCESS}` },
                React.createElement(FinalStep, { isSuccess: true, stepData: successPageModel, headerLabel: t('returnForm_success_header'), descriptionLabel: t('returnForm_success_description'), navigate: history.push, pdfFile: cachedData.pdfFile })),
            React.createElement(Route, { path: `/${StepRoutes.END_STEP_FAIL}` },
                React.createElement(FinalStep, { isSuccess: false, stepData: errorPageModel, headerLabel: t('returnForm_error_header'), descriptionLabel: t('returnForm_error_description'), navigate: history.push })))));
});
ReturnForm.displayName = 'ReturnForm_AEMComponent';
